import React, { useEffect, useMemo, useState } from "react";
import { Card, Input, Select, Tag, Space, Row, Col, Badge } from "antd";
import { SearchOutlined, FileProtectOutlined, CheckCircleFilled } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageTransition from "../../components/ui/PageTransition";
import PageHeader from "../../components/ui/PageHeader";
import EmptyState from "../../components/ui/EmptyState";

// The policies that apply to the signed-in employee (their region and department), grouped by function.
const Policies = () => {
  const { request } = useRequest();
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [search, setSearch] = useState("");
  const [fn, setFn] = useState(undefined);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    request({
      url: apiPath.policies,
      method: "GET",
      onSuccess: (res) => {
        setPolicies(res?.data?.data || []);
        setLoaded(true);
      },
      onError: () => setLoaded(true),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const functions = useMemo(() => [...new Set(policies.map((p) => p.function))].sort(), [policies]);
  const q = search.trim().toLowerCase();
  const shown = policies.filter((p) => (!fn || p.function === fn) && (!q || `${p.title} ${p.summary} ${p.policyType?.name || ""}`.toLowerCase().includes(q)));
  const pending = policies.filter((p) => p.requiresAcknowledgement && !p.acknowledged).length;
  const grouped = functions.map((f) => ({ f, items: shown.filter((p) => p.function === f) })).filter((g) => g.items.length);

  return (
    <PageTransition>
      <PageHeader title="Policies" subtitle="Company policies that apply to you" />
      {pending ? (
        <div style={{ background: "#FFF7E6", border: "1px solid #FFE0A3", borderRadius: 12, padding: "10px 16px", marginBottom: 16, color: "#8A5A00" }}>
          You have <b>{pending}</b> {pending === 1 ? "policy" : "policies"} waiting for your acknowledgement.
        </div>
      ) : null}
      <Space style={{ marginBottom: 16 }} wrap>
        <Input allowClear prefix={<SearchOutlined />} placeholder="Search policies" style={{ width: 260 }} value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select allowClear placeholder="All functions" style={{ width: 200 }} value={fn} onChange={setFn} options={functions.map((f) => ({ value: f, label: f }))} />
      </Space>
      {!loaded ? null : grouped.length ? (
        grouped.map(({ f, items }) => (
          <div key={f} style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 12, letterSpacing: ".05em", textTransform: "uppercase", color: "#6B7280", marginBottom: 8 }}>{f}</div>
            <Row gutter={[12, 12]}>
              {items.map((p) => (
                <Col xs={24} md={12} lg={8} key={p._id}>
                  <Card hoverable onClick={() => navigate(`/policies/${p._id}`)} bodyStyle={{ padding: 16 }} style={{ height: "100%", borderRadius: 14 }}>
                    <div style={{ display: "flex", gap: 12 }}>
                      <div style={{ width: 38, height: 38, borderRadius: 10, background: "#EEF2FF", color: "#4F46E5", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                        <FileProtectOutlined />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{p.title}</div>
                        <div style={{ color: "#6B7280", fontSize: 12.5, margin: "4px 0 8px" }}>{p.summary || p.policyType?.name}</div>
                        <Space size={4} wrap>
                          {p.policyType?.name ? <Tag style={{ margin: 0 }}>{p.policyType.name}</Tag> : null}
                          {p.requiresAcknowledgement ? (
                            p.acknowledged ? <Tag color="green" style={{ margin: 0 }}><CheckCircleFilled /> Acknowledged</Tag> : <Badge status="warning" text="Needs your acknowledgement" />
                          ) : null}
                        </Space>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        ))
      ) : (
        <Card>
          <EmptyState message={policies.length ? "No policies match your search" : "No policies have been shared with you yet"} />
        </Card>
      )}
    </PageTransition>
  );
};

export default Policies;
